export const useWadUtils = () => {
  const {dayJsTz, dayJsTzIsToday, dayJsTzIsYesterday} = useCurrentTime();

  const getOrdinal = (n: number) => {
    if (n > 3 && n < 21) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  }
  const formatDateRange = (start: string, end: string, tz?: string | null) => {
    // Same 'UTC' fallback as the event surfaces (agenda day tabs/bucketing) — without it a
    // missing timezone silently falls back to the build default zone (Europe/Berlin in prod).
    const startDate = dayJsTz(start, tz ?? 'UTC');
    const endDate = dayJsTz(end, tz ?? 'UTC');
    const month = startDate.format('MMMM');
    const startDay = startDate.date();
    const endDay = endDate.date();
    const year = startDate.format('YYYY');
    return startDay === endDay ? `${month} ${getOrdinal(startDay)}, ${year}` : `${month} ${startDay}–${getOrdinal(endDay)}, ${year}`;
  }
  // `tz` is the zone to render in - for notifications, the event's venue zone, since the API
  // already renders the times inside the body in it. Without it dayjs falls back to the build
  // default zone (Europe/Berlin in prod), which is what every notification used to be stamped in.
  // The today/yesterday checks take the same zone on purpose: comparing a venue-zone timestamp
  // against a Berlin "today" makes a late-evening notification read as the wrong day.
  const formatPastDateToHuman = (dateInput: string, tz?: string | null) => {
    const zone = tz ?? undefined; // the dayJsTz helpers take string | undefined; the API field is nullable
    const date = dayJsTz(dateInput, zone);
    const now = dayJsTz(); // no zone needed - the diff below is between two absolute instants
    const diffMinutes = now.diff(date, 'minute');
    if (diffMinutes < 60) {
      return date.fromNow();
    }
    if (dayJsTzIsToday(date, zone)) {
      return `Today at ${date.format('h:mm A')}`;
    }
    if (dayJsTzIsYesterday(date, zone)) {
      return `Yesterday at ${date.format('h:mm A')}`;
    }
    return `${date.format('MMMM Do, YYYY')} at ${date.format('h:mm A')}`;
  }

  /**
   * `internalPath` keeps the query and hash, because they are often the whole point of the
   * link: a chat notification points at the attendee page with ?scrollTo=chat, and the
   * pathname alone lands at the top of that page with the thread far below. Push
   * notifications were unaffected — the service worker navigates with the full URL — so
   * only in-app clicks lost it.
   *
   * `internalPathname` is the same URL without them, for callers matching against a route
   * rather than navigating to one.
   */
  const getUrlInfo = (url: string): {isInternal: boolean, internalPath: string, internalPathname: string} => {
    try {
      const target = new URL(url, window.location.origin);
      const isInternal = target.origin === window.location.origin;

      return {
        isInternal,
        internalPath: isInternal ? `${target.pathname}${target.search}${target.hash}` : '',
        internalPathname: isInternal ? target.pathname : '',
      }
    } catch (e) {
      return {
        isInternal: false,
        internalPath: '',
        internalPathname: '',
      }
    }
  }

  return {
    getOrdinal,
    formatDateRange,
    formatPastDateToHuman,
    getUrlInfo
  }
}
